/**
 * Sends SPA errors to the backend, which forwards them to VeloX.
 *
 * The reporter deliberately does not use ApiClient: it has to work both in global handlers
 * outside React and at the moment an axios request has already failed.
 */

import { describeRequestContext, readDealerCode } from './RequestContext';

const CLIENT_ERROR_PATH = '/api/v1/logs/client-error';

/** Identical errors within this window are treated as duplicates and are not sent. */
const DUPLICATE_WINDOW_MS = 5000;

const MAX_MESSAGE_LENGTH = 2000;

/** Cap on errors buffered before the backend address is known, so a failing app cannot grow it without bound. */
const MAX_PENDING_REPORTS = 20;

export type ClientErrorLevel = 'error' | 'warning';

export interface ClientErrorReport {
  readonly message: string;
  readonly details?: string;
  readonly level?: ClientErrorLevel;
  readonly statusCode?: number;
  readonly requestPath?: string;
  /** Masked summary of what the failed request asked for. */
  readonly requestContext?: string;
  readonly dealerCode?: string;
}

let backendOrigin: string | null = null;

const recentReports = new Map<string, number>();

/** Errors raised before the backend address became known. */
const pendingPayloads: string[] = [];

/**
 * Sets the backend address and flushes everything buffered so far. The origin is only known
 * from service-config, while errors can occur before the first render.
 */
export function configureErrorReporting(urlOrigin: string): void {
  backendOrigin = urlOrigin.replace(/\/+$/, '');

  pendingPayloads.splice(0, pendingPayloads.length).forEach(send);
}

export function reportClientError(report: ClientErrorReport): void {
  // An error of the logging endpoint itself is not reported: otherwise a failed send would
  // produce another send.
  if (report.requestPath && report.requestPath.indexOf(CLIENT_ERROR_PATH) !== -1) {
    return;
  }

  if (isDuplicate(report)) {
    return;
  }

  // The payload is built now rather than at flush time, so pageUrl and timestamp describe
  // the moment of the error.
  const body = JSON.stringify({
    message: report.message.slice(0, MAX_MESSAGE_LENGTH),
    details: report.details,
    level: report.level || 'error',
    statusCode: report.statusCode,
    requestPath: report.requestPath,
    requestContext: report.requestContext,
    pageUrl: window.location.href,
    dealerCode: report.dealerCode,
    timestamp: new Date().toISOString(),
  });

  if (!backendOrigin) {
    if (pendingPayloads.length < MAX_PENDING_REPORTS) {
      pendingPayloads.push(body);
    }
    return;
  }

  send(body);
}

function send(body: string): void {
  try {
    // keepalive lets the log be delivered even when the error happens while leaving the page.
    void fetch(`${backendOrigin}${CLIENT_ERROR_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Logging must never surface to the user.
  }
}

export interface ApiErrorLike {
  readonly message?: string;
  /** A number for an HTTP response, and a string such as 'ERR_NETWORK' when there was none. */
  readonly status?: number | string;
  readonly url?: string;
  readonly data?: unknown;
  /** The payload that was sent, used to say what the failed call was asked for. */
  readonly requestData?: unknown;
}

export function reportApiError(error: ApiErrorLike): void {
  // A backend message such as "Reg No not found in system" cannot be acted on without knowing
  // which number was sent, so a masked summary of the request travels with the report.
  const statusCode = typeof error.status === 'number' ? error.status : undefined;

  reportClientError({
    // A 4xx is the backend answering about what the customer typed (unknown registration
    // number, several vehicles on one number), not a fault. Kept, since the request summary
    // makes it useful, but at warning level so it stops drowning out real errors.
    level: statusCode !== undefined && statusCode >= 400 && statusCode < 500 ? 'warning' : 'error',
    message: `API request failed: ${error.message || 'unknown error'}`,
    details: describeUnknown(error.data),
    statusCode,
    requestPath: error.url,
    requestContext: describeRequestContext(error.requestData),
    dealerCode: readDealerCode(error.requestData),
  });
}

/** Name of this app's bundle (see webpack.config.js), present in the URL of every frame it runs. */
const BUNDLE_NAME = 'onlinebooking-feature-app';

/**
 * The Feature App runs inside vw.co.za next to the site's own scripts, and window-level handlers
 * see all of their errors too — analytics tags, chat widgets, cross-origin "Script error." — which
 * outnumbered this app's errors by hundreds to one. Only errors whose origin points at this
 * app's bundle are reported.
 */
function isOwnError(...sources: (string | undefined)[]): boolean {
  return sources.some((source) => !!source && source.indexOf(BUNDLE_NAME) !== -1);
}

/**
 * Installs handlers for the errors that reach neither react-query nor the ErrorBoundary:
 * errors outside React and unhandled promises. Returns a function that removes the handlers.
 */
export function installGlobalErrorHandlers(): () => void {
  const handleError = (event: ErrorEvent): void => {
    if (!isOwnError(event.filename, event.error && event.error.stack)) {
      return;
    }

    reportClientError({
      message: event.message || 'Unhandled error',
      details:
        (event.error && event.error.stack) ||
        `${event.filename || 'unknown'}:${event.lineno || 0}:${event.colno || 0}`,
    });
  };

  const handleRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;

    if (!isOwnError(reason && reason.stack)) {
      return;
    }

    const reasonMessage = reason && reason.message ? String(reason.message) : undefined;

    reportClientError({
      message: reasonMessage ? `Unhandled rejection: ${reasonMessage}` : 'Unhandled promise rejection',
      details: (reason && reason.stack) || describeUnknown(reason),
    });
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
}

function isDuplicate(report: ClientErrorReport): boolean {
  const now = Date.now();

  recentReports.forEach((reportedAt, key) => {
    if (now - reportedAt > DUPLICATE_WINDOW_MS) {
      recentReports.delete(key);
    }
  });

  // The request summary is part of the signature: two lookups that differ only by the number
  // that was typed are separate errors, not a repeat of one.
  const signature = [
    report.message,
    report.statusCode || '',
    report.requestPath || '',
    report.requestContext || '',
  ].join('|');

  if (recentReports.has(signature)) {
    return true;
  }

  recentReports.set(signature, now);
  return false;
}

function describeUnknown(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  try {
    return typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    return String(value);
  }
}
